import { firebaseDb } from "@app/config/firebase";
import {
  BRIEF_INFLUENCER_V2_SEARCH_QUERY_KEY,
  BRIEF_LIST_QUERY_KEY,
} from "@entities/brief/lib/constants";
import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { Alert, Button } from "@shared/ui";
import { useQueryClient } from "@tanstack/react-query";
import { doc, onSnapshot } from "firebase/firestore";
import { RefreshCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface BriefVersionDoc {
  version?: number;
  lastUpdatedByUserId?: string;
  updatedAt?: { toDate: () => Date };
}

export default function ChangeAlert({
  isCampaignBrief = false,
}: {
  isCampaignBrief?: boolean;
}) {
  const { briefId } = useBriefStore();
  const { userDetails } = useGetMe();
  const userId = userDetails?.userEntity?.id;
  const [showChangeAlert, setShowChangeAlert] = useState(false);
  const latestVersionRef = useRef<number>(0);
  const syncedVersionRef = useRef<number>(0);
  const isInitializedRef = useRef(false);
  const queryClient = useQueryClient();

  const handleRefresh = () => {
    setShowChangeAlert(false);
    syncedVersionRef.current = latestVersionRef.current;

    queryClient.invalidateQueries({
      queryKey: ["brief"],
      exact: false,
      type: "all",
    });

    queryClient.invalidateQueries({
      queryKey: [...BRIEF_LIST_QUERY_KEY],
      exact: false,
      type: "all",
    });
    queryClient.invalidateQueries({
      queryKey: [...BRIEF_INFLUENCER_V2_SEARCH_QUERY_KEY],
      exact: false,
      type: "all",
    });
  };

  useEffect(() => {
    if (!briefId) return;

    const briefRef = doc(
      firebaseDb,
      isCampaignBrief ? "campaign" : "brief",
      briefId
    );
    // console.log("briefRef", briefRef?.);
    const unsubscribe = onSnapshot(briefRef, (snapshot) => {
      const data = snapshot.data() as BriefVersionDoc | undefined;
      const version = data?.version ?? 0;
      const lastUpdatedByUserId = data?.lastUpdatedByUserId;

      latestVersionRef.current = version;

      if (!isInitializedRef.current) {
        syncedVersionRef.current = version;
        isInitializedRef.current = true;
        return;
      }

      if (
        version !== syncedVersionRef.current &&
        lastUpdatedByUserId &&
        userId &&
        lastUpdatedByUserId !== userId
      ) {
        setShowChangeAlert(true);
      }
    });

    return () => {
      unsubscribe();
      setShowChangeAlert(false);
      syncedVersionRef.current = 0;
      isInitializedRef.current = false;
      latestVersionRef.current = 0;
    };
  }, [briefId, userId, isCampaignBrief]);

  // console.log(
  //   "showChangeAlert",
  //   syncedVersionRef,
  //   latestVersionRef,
  //   showChangeAlert
  // );
  return (
    <>
      {showChangeAlert && (
        <Alert
          type="info"
          message="There are changes to the brief. Please refresh to see the latest changes."
          showIcon
          style={{
            color: "#1d4ed8",
            backgroundColor: "#eff6ff",
            fontSize: 12,
            marginBottom: 10,
          }}
          action={
            <Button
              onClick={() => handleRefresh()}
              icon={<RefreshCcw size={16} />}
              size="small"
              type="link"
            >
              Refresh
            </Button>
          }
        />
      )}
    </>
  );
}
